import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, CRUDActionEnum, EventResponseStatusEnum, EventsSortByEnum, StatusStateEnum } from "~/types/enums";
import eventSelectors from "~/store/events/selectors";
import eventsActionCreators from "~/store/events/actions";
import { Badge, Dropdown, Icon, Picture, Spinner, Table, Text } from "~/components-shared";
import lists from "~/lib/lists";
import { resolveDates } from "~/lib/helpers";
import useAbacExt from "~/hooks/abac";
import googleCalendar from "~/assets/i/logos/google-calendar-icon.svg";
import styled, { useTheme } from "styled-components";
const StyledPicture = styled(Picture)`
  vertical-align: middle;
`;
const EventTable = () => {
  const theme = useTheme();
  const {
    hasAfter
  } = useSelector(eventSelectors.getPagination);
  const events = useSelector(eventSelectors.getEvents);
  const status = useSelector(eventSelectors.getStatus);
  const sort = useSelector(eventSelectors.getSort);
  const {
    userHasOneOf
  } = useAbacExt();
  const dispatch = useDispatch();
  const {
    t
  } = useTranslation();
  const columnCount = 7;
  useEffect(() => {
    dispatch(eventsActionCreators.fetchStarted());
  }, [dispatch]);
  const handleRead = eventId => {
    dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.READ, eventId));
  };
  const handleUpdate = eventId => {
    dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.UPDATE, eventId));
  };
  const handleDelete = eventId => {
    dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.DELETE, eventId));
  };
  const handleSort = newSort => {
    dispatch(eventsActionCreators.sorted(newSort));
  };
  const handleLoadMore = () => {
    dispatch(eventsActionCreators.loadedMore());
  };
  if (!events.length && status === StatusStateEnum.LOADING) {
    return /*#__PURE__*/React.createElement(Spinner, {
      center: true,
      s: {
        my: 5
      }
    });
  }
  return /*#__PURE__*/React.createElement(Table, {
    fontSize: 3.2,
    tableProps: {
      style: {
        minWidth: "650px"
      }
    }
  }, /*#__PURE__*/React.createElement(Table.Header, null, /*#__PURE__*/React.createElement(Table.Tr, null, /*#__PURE__*/React.createElement(Table.Th, {
    width: "50px"
  }), /*#__PURE__*/React.createElement(Table.Th, {
    sort: sort,
    sortBy: [EventsSortByEnum.BY_NAME_ASC, EventsSortByEnum.BY_NAME_DESC],
    onSort: handleSort
  }, t("EventTable.eventName")), /*#__PURE__*/React.createElement(Table.Th, {
    sort: sort,
    sortBy: [EventsSortByEnum.BY_TYPE_ASC, EventsSortByEnum.BY_TYPE_DESC],
    onSort: handleSort
  }, t("EventTable.type")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "200px",
    sort: sort,
    sortBy: [EventsSortByEnum.BY_DATE_ASC, EventsSortByEnum.BY_DATE_DESC],
    onSort: handleSort
  }, t("EventTable.time")), /*#__PURE__*/React.createElement(Table.Th, {
    sort: sort,
    sortBy: [EventsSortByEnum.BY_ATTENDEE_COUNT_ASC, EventsSortByEnum.BY_ATTENDEE_COUNT_DESC],
    onSort: handleSort
  }, t("EventTable.attendees")), /*#__PURE__*/React.createElement(Table.Th, null, t("EventTable.going")), userHasOneOf([AuthoritiesEnum.EVENTS_WRITE]) && /*#__PURE__*/React.createElement(Table.Th, null), /*#__PURE__*/React.createElement(Table.Th, null))), /*#__PURE__*/React.createElement(Table.Body, null, events.length ? /*#__PURE__*/React.createElement(React.Fragment, null, events.map(event => /*#__PURE__*/React.createElement(Table.Tr, {
    key: event.id
  }, /*#__PURE__*/React.createElement(Table.Td, null), /*#__PURE__*/React.createElement(Table.Td, {
    onClick: () => handleRead(event.id)
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    fontSize: 3.5,
    weight: "semiBold",
    s: {
      mr: 2
    }
  }, event.name), event.googleEventId && /*#__PURE__*/React.createElement(StyledPicture, {
    src: googleCalendar,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("DailyEventListCard.fromGoogleCalendar"),
    width: "1.6rem",
    height: "1.6rem",
    color: "transparent",
    s: {
      mr: 2
    }
  }), !!event.eventSurveys.length && /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold",
    s: {
      mr: 2
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "document",
    color: theme.colors.text.secondary,
    scale: 3.5
  }), " ", event.eventSurveys.length)), /*#__PURE__*/React.createElement(Table.Td, {
    onClick: () => handleRead(event.id)
  }, /*#__PURE__*/React.createElement(Badge, {
    color: lists.events.types.colors(theme)[event.type],
    fontSize: 2.8
  }, lists.events.types.names[event.type])), /*#__PURE__*/React.createElement(Table.Td, {
    onClick: () => handleRead(event.id)
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: "textSecondary"
  }, resolveDates(event.dateFrom, event.dateTo))), /*#__PURE__*/React.createElement(Table.Td, {
    onClick: () => handleRead(event.id)
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: "textSecondary"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "people",
    color: theme.colors.text.secondary,
    scale: 3
  }), " ", event.attendees.length, " ", /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: theme.colors.secondary.main
  }, "(", event.attendees.filter(attendee => attendee.active).length, ")"))), /*#__PURE__*/React.createElement(Table.Td, {
    onClick: () => handleRead(event.id)
  }, /*#__PURE__*/React.createElement(Badge, {
    color: event.attendees.find(_ref => {
      let {
        self
      } = _ref;
      return self;
    })?.responseStatus === EventResponseStatusEnum.ACCEPTED ? "success" : "grey",
    fontSize: 2.8
  }, lists.events.response.names[event.attendees.find(_ref2 => {
    let {
      self
    } = _ref2;
    return self;
  })?.responseStatus || EventResponseStatusEnum.NEEDS_ACTION])), userHasOneOf([AuthoritiesEnum.EVENTS_WRITE], event.createdBy.id) && /*#__PURE__*/React.createElement(Table.Td, null, /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      fullWidth: false,
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    })
  }, _ref3 => {
    let {
      isOpen,
      onItemClick
    } = _ref3;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHasOneOf([AuthoritiesEnum.EVENTS_WRITE], event.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleUpdate(event.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "pencil",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), userHasOneOf([AuthoritiesEnum.EVENTS_WRITE], event.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleDelete(event.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "bin",
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete")));
  })), /*#__PURE__*/React.createElement(Table.Td, null))), hasAfter && /*#__PURE__*/React.createElement(Table.LoadMore, {
    colSpan: columnCount,
    onClick: handleLoadMore,
    isLoading: status === StatusStateEnum.LOADING
  })) : /*#__PURE__*/React.createElement(Table.Tr, null, /*#__PURE__*/React.createElement(Table.Td, {
    colSpan: columnCount,
    noHover: true,
    center: true
  }, /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 5,
    align: "center",
    display: "block",
    s: {
      my: 5
    }
  }, t("EventTable.noEventsFound"))))));
};
export default EventTable;